import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import styles from './diary-group-edit.module.css';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import ErrorComponent from '../../components/error/error';
import Form from '../../components/form/form';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';
import ToggleButton from '../../components/toggle-button/toggle-button';

// Form Data
import { formDataDiaryCreate } from '../../form-data/form-data-diary-create';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import DiaryService from '../../services/diary-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

// Helpers
import { getTodayDateString } from '../../helpers/absence-helper';
import { getFirstAndSecondName } from '../../helpers/string-format-helper';
import {
  classroomDayOf,
  classroomDayQuery,
  contentSummary,
  contentToFormData,
  diaryGroupByline,
  diaryGroupErrorMessage,
  draftPublishBlock,
  editGroupRequest,
  formDataToContent,
  isDiaryChanged,
  splitNotice,
  splitStudentUuids,
  staffNamesOf,
  studentNamesOf,
} from '../../helpers/daily-records-helper';

const DiaryGroupEdit = () => {
  const { classroomUuid, groupId } = useParams<{ classroomUuid: string; groupId: string }>();
  const [searchParams] = useSearchParams();
  const today = getTodayDateString();
  // Um diário enviado se edita em qualquer dia; sem dia, é o de hoje.
  const day = classroomDayOf(searchParams.get('dia'), today);
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const [error, setError] = useState(false);
  const [diaryService, setDiaryService] = useState<DiaryService | null>(null);

  const [dayGroups, setDayGroups] = useState<ClassroomDiaryGroup[]>([]);
  const [group, setGroup] = useState<ClassroomDiaryGroup>(null);
  const [form, setForm] = useState<FormParams>(null);
  const [formData, setFormData] = useState<DiaryFormData['formData']>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [renotify, setRenotify] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const classroomPath = `/diarios/turma/${classroomUuid}${classroomDayQuery(day, today)}`;
  const backToClassroom = () => navigate(classroomPath);

  const load = (service: DiaryService) =>
    service.listDiaryGroups(classroomUuid, day).then((groups) => {
      const found = groups.find((candidate) => candidate.id === groupId);
      if (!found) {
        setToastContent({
          isOpen: true,
          text: 'O diário não foi encontrado: ele pode ter sido separado ou descartado.',
          type: 'warning',
        });
        backToClassroom();
        return;
      }

      setDayGroups(groups);
      setGroup(found);
      setForm(formDataDiaryCreate(found.mode, found.content));
      setFormData(contentToFormData(found.content));
      setSelected(found.entries.map((entry) => entry.student.uuid));
    });

  useEffect(() => {
    new SchoolService()
      .getSchoolById(new StorageService().getEmployee().schoolId)
      .then((school) => {
        const service = new DiaryService(school.uuid);
        setDiaryService(service);

        return load(service);
      })
      .catch(() => setError(true));
  }, [classroomUuid, groupId, day]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    const parsed = parseInt(value);
    let newValue: string | number = value;
    if (id !== 'details') newValue = value === 'default' ? null : isNaN(parsed) ? value : parsed;

    setFormData((previous) => ({ ...previous, [id]: newValue }));
  };

  const toggleStudent = (studentUuid: string, checked: boolean) =>
    setSelected((previous) =>
      checked ? [...previous, studentUuid] : previous.filter((selectedUuid) => selectedUuid !== studentUuid)
    );

  // Nunca reenvia pelo professor: fechar o aviso deixa o que ele escreveu no formulário.
  const offerReload = () =>
    setModalContent({
      title: 'Diário alterado por um colega',
      modalContent: (
        <p>
          Um colega salvou este diário enquanto você editava, e nada do que você fez foi salvo. Feche este aviso para
          copiar o que escreveu, ou recarregue para ver o que foi salvo: recarregar descarta o que está no formulário.
        </p>
      ),
      submitButtonTitle: 'recarregar o diário',
      submitButtonType: 'primary',
      submitData: null,
      onSubmitButtonClick: () => load(diaryService).catch(() => Promise.reject('Houve um erro ao recarregar o diário.')),
      isOpen: true,
    });

  const showError = (err: any, fallback: string) => {
    if (isDiaryChanged(err)) {
      offerReload();
      return;
    }

    setToastContent({
      isOpen: true,
      text: diaryGroupErrorMessage(err, studentNamesOf(group), staffNamesOf(dayGroups)) || fallback,
      type: 'error',
    });
  };

  const editRequest = () => editGroupRequest(group, formDataToContent(formData, group.mode), selected, renotify);

  const handleSave = () => {
    const request = editRequest();
    if (!request) return;

    setIsSaving(true);
    diaryService
      .editDiaryGroup(group.id, request)
      .then(() => {
        const text =
          group.status === 'Draft'
            ? 'Rascunho salvo!'
            : renotify
            ? 'Diário salvo! Os responsáveis serão notificados de novo.'
            : 'Diário salvo, sem notificar os responsáveis.';
        setToastContent({ isOpen: true, text, type: 'success' });
        backToClassroom();
      })
      .catch((err) => showError(err, 'Houve um erro ao salvar o diário.'))
      .finally(() => setIsSaving(false));
  };

  // Com mudanças, o PATCH salva (e separa a seleção) e o grupo que ele devolve é enviado inteiro;
  // sem mudanças, o envio com studentUuids separa e envia de uma vez.
  const handlePublish = () => {
    const request = editRequest();
    setIsSaving(true);

    if (!request) {
      diaryService
        .publishDiaryDraft(group.id, group.version, splitStudentUuids(group, selected))
        .then(() => {
          setToastContent({ isOpen: true, text: 'Diário enviado!', type: 'success' });
          backToClassroom();
        })
        .catch((err) => showError(err, 'Houve um erro ao enviar o rascunho.'))
        .finally(() => setIsSaving(false));
      return;
    }

    diaryService
      .editDiaryGroup(group.id, request)
      .catch((err) => {
        showError(err, 'Houve um erro ao salvar o rascunho.');
        return null;
      })
      .then((saved) => {
        if (!saved) return;

        return diaryService
          .publishDiaryDraft(saved.id, saved.version)
          .then(() => setToastContent({ isOpen: true, text: 'Diário enviado!', type: 'success' }))
          .catch((err) => {
            // O rascunho já mudou (e talvez foi separado): o formulário não descreve mais o que está salvo.
            const reason = diaryGroupErrorMessage(err, studentNamesOf(group), staffNamesOf(dayGroups));
            setToastContent({
              isOpen: true,
              text: `As mudanças foram salvas no rascunho, mas ele não foi enviado. ${reason || ''}`.trim(),
              type: 'error',
            });
          })
          .then(backToClassroom);
      })
      .finally(() => setIsSaving(false));
  };

  // Tudo ou nada: a seleção vale para salvar e enviar, nunca para descartar.
  const handleDiscard = () => {
    const names = group.entries.map((entry) => entry.student.name || 'Aluno').join(', ');

    setModalContent({
      title: 'Descartar rascunho',
      modalContent: (
        <p>{`Descartar o rascunho de ${names}? O que foi escrito será perdido para todos.`}</p>
      ),
      submitButtonTitle: 'descartar',
      submitButtonType: 'primary',
      submitData: null,
      onSubmitButtonClick: () =>
        diaryService
          .discardDiaryDraft(group.id, group.version)
          .then(() => {
            setToastContent({ isOpen: true, text: 'Rascunho descartado!', type: 'success' });
            backToClassroom();
          })
          .catch((err) =>
            Promise.reject(
              diaryGroupErrorMessage(err, studentNamesOf(group), staffNamesOf(dayGroups)) ||
                'Houve um erro ao descartar o rascunho.'
            )
          ),
      isOpen: true,
    });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!group) return <Loading type='contentFull' />;

  const isDraft = group.status === 'Draft';
  const publishBlock = isDraft ? draftPublishBlock(day, today) : null;
  // Um rascunho de dia anterior nunca mais será enviado: só se consulta ou descarta.
  const isPastDraft = Boolean(publishBlock);
  const notice = isPastDraft ? null : splitNotice(group, selected);
  const request = editRequest();
  // Como no formulário novo: um diário enviado precisa de algum conteúdo, um rascunho não.
  const hasContent = Object.keys(formDataToContent(formData, group.mode)).length > 0;
  const canSave = !isSaving && selected.length > 0;
  const summary = contentSummary(group.content);

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithActionButtons}
      biggerSide={BaseContentContainerBiggerSide.right}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      {isPastDraft ? (
        <div className={styles.readOnly}>
          <h3>Rascunho de {format(parseISO(day), 'dd/MM/yyyy')}</h3>
          {summary && <p className={styles.summary}>{summary}</p>}
          {group.content.details && <p className={styles.details}>{group.content.details}</p>}
          {!summary && !group.content.details && <p className={styles.summary}>Rascunho sem nada preenchido.</p>}
        </div>
      ) : (
        // O Form lê os valores só ao montar: recarregar o grupo precisa de um Form novo.
        <Form
          key={`${group.id}-${group.version}`}
          formData={form}
          validations={{}}
          handleOnChange={handleFormOnChange}
          title={isDraft ? 'Continuar rascunho' : 'Editar diário'}
        />
      )}
      <div className={styles.students}>
        <h3>{isDraft ? 'Alunos do rascunho' : 'Alunos do diário'}</h3>
        <p className={styles.byline}>{diaryGroupByline(group)}</p>
        {group.entries.length > 1 && !isPastDraft && (
          <p className={styles.hint}>Desselecione alunos para mudar só os outros.</p>
        )}
        <div className={styles.studentButtons}>
          {group.entries.map((entry) => {
            const isSelected = selected.includes(entry.student.uuid);

            return (
              <ToggleButton
                key={entry.entryId}
                value={getFirstAndSecondName(entry.student.name || 'Aluno')}
                selected={isSelected}
                disabled={isPastDraft}
                onSelected={() => toggleStudent(entry.student.uuid, !isSelected)}
              />
            );
          })}
        </div>
        {notice && (
          <p className={styles.notice} role='note'>
            {notice}
          </p>
        )}
        {/* A API não salva uma edição sem mudança, nem para separar a seleção. */}
        {notice && !request && <p className={styles.hint}>Para salvar só a seleção, mude algo no diário.</p>}
        {selected.length === 0 && <p className={styles.notice}>Escolha ao menos um aluno.</p>}
        {!isDraft && (
          <label className={styles.renotify}>
            <input type='checkbox' checked={renotify} onChange={(e) => setRenotify(e.target.checked)} />
            Notificar os responsáveis de novo
          </label>
        )}
        {!isDraft && <p className={styles.hint}>Desmarque para uma correção silenciosa, como um erro de digitação.</p>}
        {publishBlock && <p className={styles.notice}>{publishBlock}</p>}
      </div>
      <ButtonsBar pattern='together' sideAlign='right' verticalAlign='bottom'>
        <Link to={classroomPath}>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        {isDraft && (
          <SubmitButton
            value='descartar'
            variant='terciary'
            onClick={handleDiscard}
            disabled={isSaving}
          />
        )}
        {isDraft ? (
          !isPastDraft && (
            <>
              <SubmitButton value='salvar rascunho' variant='secondary' onClick={handleSave} disabled={!canSave || !request} />
              <SubmitButton
                value={notice ? 'separar e enviar' : 'enviar'}
                variant='primary'
                onClick={handlePublish}
                disabled={!canSave || !hasContent}
              />
            </>
          )
        ) : (
          <SubmitButton
            value='salvar'
            variant='primary'
            onClick={handleSave}
            disabled={!canSave || !hasContent || !request}
          />
        )}
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default DiaryGroupEdit;
